import React from 'react';
import './StreakBadgeAtom.scss';

export type StreakBadgeTone = 'danger' | 'warning' | 'success' | 'neutral';

export interface StreakBadgeAtomProps {
  /** The streak count to display (e.g. consecutive missed periods). */
  count: number;
  /** Already-localized suffix label, e.g. "in a row". Rendered after the count. */
  label?: string;
  /** Icon class — full PrimeIcons class string. Default "pi pi-bolt". */
  icon?: string;
  /** Visual tone. Consumers supply the semantic meaning. */
  tone?: StreakBadgeTone;
  /** Threshold below which the badge renders nothing. */
  minCount?: number;
  /** Optional aria-label override. Defaults to `{count} {label}` if label set, else `Streak: {count}`. */
  ariaLabel?: string;
  className?: string;
}

const StreakBadgeAtom: React.FC<StreakBadgeAtomProps> = ({
  count,
  label,
  icon = 'pi pi-bolt',
  tone = 'danger',
  minCount = 2,
  ariaLabel,
  className = '',
}) => {
  if (count < minCount) {
    return null;
  }

  const classes = [
    'streak-badge-atom',
    `streak-badge-atom--tone-${tone}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const resolvedAriaLabel =
    ariaLabel ?? (label ? `${count} ${label}` : `Streak: ${count}`);

  return (
    <output className={classes} aria-label={resolvedAriaLabel}>
      <i className={`${icon} streak-badge-atom__icon`} aria-hidden="true" />
      <span className="streak-badge-atom__count">{count}</span>
      {label ? <span className="streak-badge-atom__label">{label}</span> : null}
    </output>
  );
};

StreakBadgeAtom.displayName = 'StreakBadgeAtom';

export default StreakBadgeAtom;
